import { QuestionKey } from '@hb/modules/global/global.constants';
import { GlobalControl, HintType } from '@hb/domain';
import { AnalyticsService } from '@hb/services/analytics/analytics.service';
import { EnergyLabelStoreService } from '@hb/modules/property/energy-label/energy-label-store.service';
import { CoApplicantPipe, EnergyLabelPipe } from '@hb/modules/global';
import { GLOBAL_STORE, GlobalInjectComponent, QUESTION_KEY } from '@hb/modules/global/abstract-global-inject.component';
import { EnergyBonusService } from '@hb/services/energy-bonus/energy-bonus.service';
import { AdditionalInfoComponent } from '@hb/modules/global/additional-info';
import { NextButtonComponent } from '@hb/modules/global/button';

import { Component, inject, OnInit } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe, NgClass } from '@angular/common';

import { EnergyBonus, EnergyLabel, PropertyType } from '@ap/ap-interface-apix-hypotheken-berekening';
import { ApContentModule } from '@ap/content';
import { ApRadioModule } from '@ap/radio';
import { ApAmountDisplayComponent } from '@ap/amounts';
import { ApInlineSvgDirective } from '@ap/inline-svg';
import { ApSpinnerComponent } from '@ap/spinner';

@Component({
    selector: 'energy-label-radio',
    templateUrl: './energy-label-radio.component.html',
    styleUrls: ['.//energy-label-radio.component.scss', '../../../global/global.scss'],
    standalone: true,
    imports: [
        ApContentModule,
        FormsModule,
        ReactiveFormsModule,
        AsyncPipe,
        ApRadioModule,
        ApAmountDisplayComponent,
        NgClass,
        ApInlineSvgDirective,
        ApSpinnerComponent,
        AdditionalInfoComponent,
        CoApplicantPipe,
        NextButtonComponent,
        EnergyLabelPipe
    ],
    providers: [
        { provide: GLOBAL_STORE, useExisting: EnergyLabelStoreService },
        { provide: QUESTION_KEY, useValue: QuestionKey.ENERGY_LABEL_RADIO }
    ]
})
export class EnergyLabelRadioComponent extends GlobalInjectComponent<EnergyLabelStoreService> implements OnInit {
    private readonly analyticsService = inject(AnalyticsService);
    private readonly energyBonusService = inject(EnergyBonusService);

    protected readonly store = inject(EnergyLabelStoreService);
    protected readonly hintType = HintType;

    questionKey = QuestionKey.ENERGY_LABEL_RADIO;
    energyBonuses: Observable<EnergyBonus[]>;
    contentLabel: string;
    propertyType: PropertyType;

    constructor() {
        super();
    }

    ngOnInit(): void {
        const state = this.store.getInitialValues();
        this.mainApplicantControl = new GlobalControl<EnergyLabel>(this.analyticsService, this.questionKey, state);

        this.subscribe();

        this.store.getEnergyLabel().subscribe((energyLabel: EnergyLabel) => {
            this.mainApplicantControl.setValue(energyLabel);
        });
        this.mainApplicantControl.onUpdate().subscribe((energyLabel) => {
            this.store.setEnergyLabel(energyLabel);
        });
    }

    subscribe(): void {
        this.store
            .getPropertyType()
            .pipe(
                tap(
                    (propertyType: PropertyType) =>
                        (this.energyBonuses = this.energyBonusService
                            .getEnergyBonuses(propertyType)
                            .pipe(map((response) => this.setEnergyBonuses(response.energy_bonuses))))
                )
            )
            .subscribe((propertyType: PropertyType) => {
                this.propertyType = propertyType;
                this.contentLabel =
                    propertyType === PropertyType.NONE
                        ? 'property.energy-label.property-none.select'
                        : 'property.energy-label.property-build.select';
            });
    }

    isSelected(energyLabel: EnergyLabel): boolean {
        return energyLabel === this.mainApplicantControl.value;
    }

    nextQuestion(): void {
        this.store.nextQuestion();
    }

    hasHint(energyLabel: EnergyLabel): boolean {
        return energyLabel === EnergyLabel.APPPP10YR;
    }

    getSvgUrl(energyLabel: EnergyLabel): string {
        return `/illustration/energy-label-${energyLabel}.svg`;
    }

    private setEnergyBonuses(energyBonuses: EnergyBonus[]): EnergyBonus[] {
        return [...energyBonuses]
            .sort((a, b) => a.energy_label.localeCompare(b.energy_label))
            .sort((a, b) => b.amount.amount - a.amount.amount);
    }
}
